import { default as logger } from './log'
import { makeTimer } from './timer-util'
import reportError from './report-error'

/**
 * Creates a scheduler that periodically fetches data from a server via the given 'fetch' and/or 'diff'
 * functions created via 'createBoundActions' (see bound-actions.js) and passes the results to the Redux store.
 *
 * There are four modes of operation:
 * 1. Big refreshes only: The 'fetch' function is called periodically and replaces the content of the associated
 *    Redux store (by using the 'forceUpdate' flag of 'fetch').
 * 2. Periodic updates only: The 'diff' function is called periodically and updates the content of the associated
 *    Redux store via 'patch' operations. This assumes that the store has been filled beforehand. The backend server
 *    must implement the Differential GET protocol.
 * 3. Initial fetch plus periodic updates: The 'fetch' function is called initially. Then, periodic 'diff' requests
 *    update the store. The backend server must implement the Differential GET protocol.
 * 4. Interleaved big refreshes and periodic updates: Starting with a 'fetch' call, runs the 'fetch' function
 *    periodically (with big delays). In between the big-refresh calls, there are multiple 'diff' calls (with
 *    smaller delays). The big-refresh period must be a multiple of the small-refresh period.
 *    The backend server must implement the Differential GET protocol.
 *
 * @param {Object} config - The scheduler configuration
 * @param {Function} [config.fetch] - A redux thunk action for complete fetches (see bound-actions.js)
 * @param {number} [config.fetchPeriod] - The time difference between two fetch runs (in milliseconds)
 * @param {Function} [config.diff] - A redux thunk action for differential fetches (see bound-actions.js)
 * @param {number} [config.diffPeriod] - The time difference between two diff runs (in milliseconds)
 * @returns {Object} The start/stop/restart/update/isActive helper functions
 */
export function createFetchScheduler(config) {
    checkParameters(config)

    let fetchFunc = config.fetch ? createWrappedFetch(config.fetch) : null
    let diffFunc = config.diff ? createWrappedDiff(config.diff) : null

    // Delay between to backend calls in milliseconds
    const callDelay = config.diffPeriod || config.fetchPeriod

    // Number of 'diff' calls to be done before a big refresh (also counting the previous big refresh)
    const diffRepeat = config.fetchPeriod && config.diffPeriod ? Math.floor(config.fetchPeriod / config.diffPeriod) : 0

    let callCount = 0 // Current number of finished calls
    let delay = 0
    let startStopCounter = 0

    let timer
    let { lastModified = Date.now() - 2 * callDelay } = config

    const subscriptions = []

    /**
     * Starts the scheduler.
     *
     * @returns {Function} A redux thunk
     */
    function start() {
        // Use the Redux middleware style, which provides access to the store's dispatch function.
        return dispatch => {
            if (isActive()) {
                return Promise.resolve()
            }
            ++startStopCounter
            callCount = 0
            delay = 0
            return scheduleTask(dispatch)
        }
    }

    /**
     * Stops the scheduler. It can be restarted.
     *
     * @returns {Function} A redux thunk
     */
    function stop() {
        return (/* dispatch */) => {
            if (!isActive()) {
                return
            }
            timer.cancel()
            timer = undefined
        }
    }

    /**
     * Updates the 'fetch' and/or 'diff' functions of the scheduler configuration.
     * If the scheduler runs, it is stopped and restarted.
     *
     * @param {Object} config - The scheduler configuration
     * @param {Function} [config.fetch] - A redux thunk action for complete fetches (see bound-actions.js)
     * @param {Function} [config.diff] - A redux thunk action for differential fetches (see bound-actions.js)
     */
    function update(config) {
        return dispatch => {
            const wasActive = isActive()
            if (wasActive) {
                dispatch(stop())
            }
            if (config.fetch) {
                fetchFunc = createWrappedFetch(config.fetch)
            }
            if (config.diff) {
                diffFunc = createWrappedDiff(config.diff)
            }
            if (wasActive) {
                return dispatch(start())
            }
            return Promise.resolve()
        }
    }

    /**
     * Restarts the scheduler.
     *
     * @returns {Function} A redux thunk
     */
    function restart() {
        return dispatch => {
            dispatch(stop())
            dispatch(start())
        }
    }

    async function scheduleTask(dispatch) {
        const orgStartStopCounter = startStopCounter

        timer = makeTimer(execTask, delay)(getNextFunction(), dispatch)
        try {
            const result = await timer.getPromise()
            if (isActive() && startStopCounter === orgStartStopCounter) {
                // Schedule the next execution only if scheduler was not stopped in between.
                // Note that function 'setInterval' is NOT an alternative, because that one does not wait
                // for the termination of the tasks (HTTP requests) when it schedules the next execution.
                scheduleTask(dispatch)
            }
            return result
        } catch (error) {
            // Avoid unhandled promise rejection. The timer promise rejects on cancelling.
            if (error.name === 'ECANCELED') {
                logger.debug('[FetchScheduler] Timer has been cancelled')
                return null
            }
            throw error
        }
    }

    /**
     * Private side-effect function that runs the task and returns its result.
     * When the task is finished, starts a new run by calling this function again.
     *
     * @private
     * @param {Function} wrappedFunc - A wrapped fetch or diff function
     * @param {Function} dispatch - The dispatch function of the Redux store
     * @returns {Promise} A promise of the result from the server call
     */
    async function execTask(wrappedFunc, dispatch) {
        let result
        let errorObject
        try {
            ++callCount
            delay = callDelay
            result = await dispatch(wrappedFunc)
        } catch (error) {
            errorObject = error
            logger.warn('[FetchScheduler] Error from server: ', error)

            if (error.status === 400) {
                logger.error('[FetchScheduler] Stopping scheduler due to invalid request')
                dispatch(stop())
                reportError(error)
            } else {
                delay += callDelay
                logger.warn(`[FetchScheduler] Schedule next run with ${delay} ms delay`)
            }
        } finally {
            subscriptions.forEach(callback =>
                callback({ result, callCount, delay, startStopCounter, lastModified, error: errorObject })
            )
        }
    }

    /**
     * Private side-effect function that decides for the GET function to use for the next cycle.
     * For more information, see "modes of operation" in the function documentation.
     *
     * @private
     * @returns {Function} The fetch or diff handler
     */
    function getNextFunction() {
        if (!diffFunc) {
            // Mode 1: Big refreshes only
            return fetchFunc
        }
        if (!fetchFunc) {
            // Mode 2 (Periodic updates only)
            return diffFunc
        }
        if (callCount === 0) {
            // Initial fetch
            return fetchFunc
        }
        if (diffRepeat === 0) {
            // Mode 3 (Initial fetch plus periodic updates)
            return diffFunc
        }
        if (callCount % diffRepeat === 0) {
            // Mode 4: Interleaved big refreshes and periodic updates -- big refresh
            return fetchFunc
        } else {
            // Mode 4: Interleaved big refreshes and periodic updates -- periodic update
            return diffFunc
        }
    }

    /**
     * Creates a function that wraps the passed fetch function. The wrapped fetch function returns
     * a promise that resolves to the fetch result.
     *
     * Purpose of the returned wrapper is to reach the same function signature for both fetch and diff.
     *
     * @private
     * @param {Function} fetch - The function to be wrapped
     * @returns {Promise} Async redux thunk
     */
    function createWrappedFetch(fetch) {
        const options = { forceUpdate: true }
        return async dispatch => {
            const fallbackTime = Date.now()
            const result = await dispatch(fetch(options))
            const { data, responseDate } = result || {}
            lastModified = Number.isInteger(responseDate) ? responseDate : fallbackTime
            return data
        }
    }

    /**
     * Creates a function that wraps the passed diff function. The wrapped diff function grabs the
     * modification timestamp, and returns a promise that resolves to the fetch result.
     *
     * Purpose of the returned wrapper is to reach the same function signature for both fetch and diff.
     *
     * @private
     * @param {Function} diff - The function to be wrapped
     * @returns {Promise} Async redux thunk
     */
    function createWrappedDiff(diff) {
        return async dispatch => {
            const fallbackTime = Date.now()
            const result = await dispatch(diff(lastModified))
            const { data, responseDate } = result || {}
            lastModified = Number.isInteger(responseDate) ? responseDate : fallbackTime
            return data
        }
    }

    function isActive() {
        return timer !== undefined
    }

    function subscribe(callback) {
        subscriptions.push(callback)
    }

    return {
        restart,
        start,
        stop,
        update,
        subscribe
    }
}

/**
 * Internal function for parameter checking.
 * It does not make use of closure variables, so it is outside the constructor function.
 *
 * @private
 * @throws {Error} Will throw for invalid config parameters
 * @param {Object} config - The createFetchScheduler function params
 * @returns {void}
 */
function checkParameters(config) {
    const errors = []
    if (!config.fetch && !config.diff) {
        errors.push('Missing functions "fetch" and/or "diff"')
    }
    if (config.diff && !config.diffPeriod) {
        errors.push('Missing parameter "diffPeriod" for "diff" function')
    }
    if (config.fetch && !config.fetchPeriod && !config.diff) {
        errors.push('Missing parameter "fetchPeriod" (or provide a "diff" function in addition)')
    }
    if (config.fetchPeriod && !Number.isInteger(config.fetchPeriod)) {
        errors.push('Parameter "fetchPeriod" must be numeric')
    }
    if (config.diffPeriod && !Number.isInteger(config.diffPeriod)) {
        errors.push('Parameter "diffPeriod" must be numeric')
    }
    if (
        config.fetchPeriod &&
        config.diffPeriod &&
        (config.fetchPeriod <= config.diffPeriod || config.fetchPeriod % config.diffPeriod > 0)
    ) {
        errors.push('"fetchPeriod" must be a multiple of "diffPeriod"')
    }
    if (errors.length > 0) {
        throw new Error(errors.join('. '))
    }
}
